import type { StaticCollider } from "@endclock/shared";
import type { CameraCollisionProbe } from "./camera.js";

/** Reuse cached gameplay collision proxies; no render-mesh raycasts are needed. */
export function createCameraCollisionProbe(
  heightAt: (x: number, z: number) => number,
  getColliders: () => readonly StaticCollider[],
): CameraCollisionProbe {
  return (x, y, z, radius) => {
    let floor = heightAt(x, z);
    // Most of the raised boom is well clear of the ground. Refine only near
    // terrain, where the eye's padded edge can intersect an approaching slope.
    if (y - radius - floor < 1) {
      floor = Math.max(floor,
        heightAt(x - radius, z), heightAt(x + radius, z),
        heightAt(x, z - radius), heightAt(x, z + radius),
      );
    }
    if (y - radius <= floor) return true;
    for (const collider of getColliders()) {
      const offsetX = x - collider.x;
      const offsetZ = z - collider.z;
      if (collider.shape === "circle") {
        if (Math.hypot(offsetX, offsetZ) <= collider.radius + radius) return true;
        continue;
      }
      const cosine = Math.cos(collider.yaw);
      const sine = Math.sin(collider.yaw);
      const localX = offsetX * cosine - offsetZ * sine;
      const localZ = offsetX * sine + offsetZ * cosine;
      if (collider.shape === "capsule") {
        const axisX = Math.max(-collider.halfLength, Math.min(collider.halfLength, localX));
        if (Math.hypot(localX - axisX, localZ) <= collider.radius + radius) return true;
      } else {
        const closestX = Math.max(-collider.halfWidth, Math.min(collider.halfWidth, localX));
        const closestZ = Math.max(-collider.halfDepth, Math.min(collider.halfDepth, localZ));
        if (Math.hypot(localX - closestX, localZ - closestZ) <= radius) return true;
      }
    }
    // Existing movement proxies have no vertical extent. Their conservative
    // extrusion keeps the camera outside walls, trunks and rocks alike.
    return false;
  };
}
